import React from 'react';
import { MessageSquare, Target, TrendingDown, Lightbulb } from 'lucide-react';
import { SectionTitle, GoldBadge } from '../Luxury';
import { formatPercent } from '../../utils/formatters';
import type { PropertyAnalysisResult } from '../../types/calculations';

interface NegotiationStrategyCardProps {
  data: PropertyAnalysisResult;
}

const MOTIVATION_CONFIG: Record<string, { color: string; bg: string; border: string }> = {
  high: { color: 'text-emerald', bg: 'bg-emerald/15', border: 'border-emerald/30' },
  medium: { color: 'text-gold', bg: 'bg-gold/15', border: 'border-gold/30' },
  low: { color: 'text-crimson', bg: 'bg-crimson/15', border: 'border-crimson/30' },
};

export const NegotiationStrategyCard: React.FC<NegotiationStrategyCardProps> = ({ data }) => {
  const offerIntel = data?.investment_intelligence?.offer_intelligence;

  if (!offerIntel) {
    return (
      <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
        <SectionTitle icon={<MessageSquare className="w-5 h-5" />}>Negotiation Strategy</SectionTitle>
        <p className="text-stone-700 text-sm">Negotiation intelligence data not available for this property.</p>
      </div>
    );
  }

  const { motivation_level, motivation_score, offers } = offerIntel;
  const motionConfig = MOTIVATION_CONFIG[motivation_level] || MOTIVATION_CONFIG.medium;

  // Compile all leverage points across all tiers, deduplicated
  const allLeveragePoints = Array.from(
    new Set(offers.flatMap((o) => o.leverage_points))
  );

  // Compute aggregate discount factor (average across tiers)
  const avgDiscount = offers.length > 0
    ? offers.reduce((sum, o) => sum + o.discount_pct, 0) / offers.length
    : 0;

  // Signals used: motivation level + any leverage points that indicate data signals
  const signals = [
    `Motivation level: ${motivation_level}`,
    `Motivation score: ${(motivation_score * 100).toFixed(0)}%`,
    `${offers.length} offer tier(s) generated`,
    ...(avgDiscount > 0 ? [`Avg discount: ${formatPercent(avgDiscount)}`] : []),
  ];

  return (
    <div className="bg-charcoal rounded-xl border border-slate-dark p-6">
      <div className="flex items-center justify-between mb-6">
        <SectionTitle icon={<MessageSquare className="w-5 h-5" />}>Negotiation Strategy</SectionTitle>
        <GoldBadge variant="gold">AI-Powered</GoldBadge>
      </div>

      {/* Top Row: Motivation Badge + Score + Discount Factor */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {/* Motivation Level */}
        <div className={`rounded-lg ${motionConfig.bg} border ${motionConfig.border} p-4 flex flex-col items-center justify-center`}>
          <div className="text-sm uppercase tracking-wider text-stone-700 mb-1">Seller Motivation</div>
          <div className={`text-xl font-bold uppercase tracking-wider ${motionConfig.color}`}>
            {motivation_level}
          </div>
        </div>

        {/* Motivation Score */}
        <div className="rounded-lg bg-charcoal border border-slate-dark p-4 flex flex-col items-center justify-center">
          <div className="text-sm uppercase tracking-wider text-stone-700 mb-1">Motivation Score</div>
          <div className="text-2xl font-bold font-mono text-ivory">
            {(motivation_score * 100).toFixed(0)}%
          </div>
          <div className="w-full mt-2 h-1.5 bg-graphite rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                motivation_score >= 0.7 ? 'bg-emerald' : motivation_score >= 0.4 ? 'bg-gold' : 'bg-crimson'
              }`}
              style={{ width: `${motivation_score * 100}%` }}
            />
          </div>
        </div>

        {/* Aggregate Discount Factor */}
        <div className="rounded-lg bg-charcoal border border-slate-dark p-4 flex flex-col items-center justify-center">
          <div className="text-sm uppercase tracking-wider text-stone-700 mb-1">Avg Discount</div>
          <div className="text-2xl font-bold font-mono text-gold">
            {formatPercent(avgDiscount)}
          </div>
        </div>
      </div>

      {/* Signals Used */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Target className="w-4 h-4 text-gold" />
          <span className="text-sm font-semibold uppercase tracking-wider text-stone-700">Signals Used</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {signals.map((signal, idx) => (
            <span
              key={idx}
              className="inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium bg-graphite border border-slate-dark text-stone-700"
            >
              {signal}
            </span>
          ))}
        </div>
      </div>

      {/* Talking Points / Leverage Points */}
      {allLeveragePoints.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Lightbulb className="w-4 h-4 text-gold" />
            <span className="text-sm font-semibold uppercase tracking-wider text-stone-700">
              Recommended Talking Points
            </span>
          </div>
          <ul className="space-y-2">
            {allLeveragePoints.map((point, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2.5 text-sm text-stone-300 bg-graphite/50 rounded-lg px-3 py-2 border border-slate-dark/50"
              >
                <TrendingDown className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
                {point}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default NegotiationStrategyCard;
